import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSearchHistory } from '../../redux/thunk/searchHistoryThunk';
import { selectSurfaceLoaded } from '../../redux/slices/searchHistorySlice';
import { selectMixpediaViewIsEmpty, clearResults } from '../../redux/slices/djpalSlice';
import { track } from '../../utils/analytics';
import useDjpalConnect from '../../hooks/useDjpalConnect';
import ArtistSearch from '../DJPal/ArtistSearch';
import TrackSearch from '../DJPal/TrackSearch';
import MixSearch from '../DJPal/MixSearch';
import TrackListSearch from '../DJPal/TrackListSearch';
import SearchResultsPage from '../DJPal/SearchResultsPage';
import MixpediaHome from '../DJPal/MixpediaHome';
import MixpediaBrowsePage from '../DJPal/MixpediaBrowsePage';
import MixpediaLibraryArtistsPage from '../DJPal/MixpediaLibraryArtistsPage';
import MixpediaArtistsGridPage from '../DJPal/MixpediaArtistsGridPage';
import LabelSearch from '../DJPal/LabelSearch';
import NavbarSearch from '../NavbarSearch';
import '../DJPal/DJPal.scss';
import './DJPalTab.scss';

// Scroll-position stores live at MODULE level (same pattern as FeedTab's
// _savedScrollTop): CategoryView unmounts this component on every tab
// switch, so component refs would lose all positions exactly when the user
// leaves and Backs into Mixpedia cross-tab.
const _scrollByNav = new Map();
const _scrollByView = new Map();

// Mixpedia shell. Query entry lives in the navbar search (desktop) or the
// inline NavbarSearch header (web/mobile); `activeSearch` routes between the
// entity/list pages, and the home rows show when nothing is open:
//   'all'       → unified results page (with scope chips)
//   'artist'    → artist page (dossier)
//   'track'     → single-track page
//   'trackList' → track list page (free-text / played-by / co-played)
//   'mix'       → mix list page (by title / by artist / featuring)
//   'label'     → label page (header + tracks on the label)
const DJPalTab = () => {
    const dispatch = useDispatch();
    const { activeSearch, searchAllQuery } = useSelector((state) => state.djpal);
    // Background DJPal token resolution — see useDjpalConnect for rationale.
    // The tab shell needs no token; pages self-connect on demand via
    // ensureToken.
    const { connectFailed, tokenError, retry: retryConnect } = useDjpalConnect();
    const historyLoaded = useSelector(selectSurfaceLoaded('mixpedia'));
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const isWeb = typeof window !== 'undefined' && window.electron && !window.electron.db;

    // Preserve scroll position across page switches. The scroll container
    // (.lower-part) stays mounted while React swaps its child; when the new
    // child is shorter, the browser clamps scrollTop to 0 and that clamp
    // sticks when the user returns. Track scrollTop continuously via a
    // listener (by the time useLayoutEffect runs the DOM has already updated
    // and the browser has clamped, so reading scrollTop there would capture
    // the wrong value).
    //
    // Two keying schemes, nav entry first:
    //  - per nav ENTRY (navId): every history step gets its own slot,
    //    maintained live by the scroll listener. Back/forward restores the
    //    exact position of the entry you return to — including same-view
    //    navigations (artist → artist) that the view key can't distinguish.
    //    Frozen while a search is in flight: the loading skeleton is short,
    //    the browser clamps scrollTop to ~0, and that clamp must not
    //    overwrite the position saved for the page being left.
    //  - per VIEW kind (activeSearch): fallback for swaps where the nav
    //    entry does NOT change (a clean-marker entry being filled by its
    //    search result).
    const scrollRef = useRef(null);
    const scrollByView = useRef(_scrollByView);
    const scrollByNav = useRef(_scrollByNav);
    const currentViewRef = useRef(activeSearch);
    const lastScrollTopRef = useRef(0);

    // navId of the current stack entry (stamped by pushNavEntry); fall back
    // to the raw index for entries that predate the stamp.
    const navKey = useSelector((state) => {
        const { navStack, navIndex } = state.app;
        return navStack[navIndex]?.navId ?? navIndex;
    });
    const isSearching = useSelector((state) => state.djpal.isSearching);
    // undefined sentinel: the first effect run after a (re)mount must take
    // the nav-step branch and restore this entry's saved position — the
    // component unmounts on tab switches, so "mount" IS the cross-tab
    // return path.
    const currentNavKeyRef = useRef(undefined);
    const isSearchingRef = useRef(isSearching);
    isSearchingRef.current = isSearching;

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;
        const onScroll = () => {
            lastScrollTopRef.current = el.scrollTop;
            if (!isSearchingRef.current) {
                scrollByNav.current.set(currentNavKeyRef.current, el.scrollTop);
            }
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    useLayoutEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const prevView = currentViewRef.current;
        const prevNavKey = currentNavKeyRef.current;
        if (prevView === activeSearch && prevNavKey === navKey) return;
        if (prevView !== activeSearch) {
            scrollByView.current.set(prevView, lastScrollTopRef.current);
        }
        // Nav step wins over view swap when both change in the same commit.
        const navStepped = prevNavKey !== navKey;
        const restored = navStepped
            ? (scrollByNav.current.get(navKey) ?? 0)
            : (scrollByView.current.get(activeSearch) ?? 0);
        el.scrollTop = restored;
        lastScrollTopRef.current = restored;
        // Seed the arrived-at entry's slot ONLY on a nav step. A same-entry
        // view swap (Show-all click switching to the mix view before its
        // search result pushes the new entry) must not overwrite the slot of
        // the entry being LEFT — that's the position Back restores to.
        if (navStepped) scrollByNav.current.set(navKey, restored);
        currentViewRef.current = activeSearch;
        currentNavKeyRef.current = navKey;
    }, [activeSearch, navKey]);

    // Populate Mixpedia recents (used by the navbar search dropdown).
    useEffect(() => {
        if (isAuthenticated && !historyLoaded) {
            dispatch(fetchSearchHistory({ surface: 'mixpedia', limit: 30 }));
        }
    }, [dispatch, isAuthenticated, historyLoaded]);

    // Home shows when the routed page has nothing open — see the selector's
    // doc comment in djpalSlice (shared with the navbar home button so the
    // two can't drift apart).
    const viewIsEmpty = useSelector(selectMixpediaViewIsEmpty);

    // Re-tapping the active Mixpedia bottom-nav tab pops back to the home
    // rows (standard mobile pattern — otherwise the home is unreachable once
    // a page is open, since the tab button itself restores the last page).
    // Already home → scroll to top, same as the For You tab. clearResults
    // goes through the nav-history middleware, which pushes the home stop so
    // Android system-back still returns to the page that was open.
    const viewIsEmptyRef = useRef(viewIsEmpty);
    viewIsEmptyRef.current = viewIsEmpty;
    useEffect(() => {
        const onRetap = (e) => {
            if (e?.detail?.key !== 'djpal') return;
            if (viewIsEmptyRef.current) {
                scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
            track('navbar_home_click', { from: 'tab_retap' });
            dispatch(clearResults());
            // Empty the inline search input's retained text (same external
            // clear hook the hub card uses).
            window.dispatchEvent(new Event('navbar-search-clear'));
            // The home renders under the default 'artist' view key, so the
            // scroll save/restore above would otherwise land it at the old
            // page's offset. Zero the stored slot (covers the layout-effect
            // restore when activeSearch changes) and reset after the
            // re-render (covers artist → home, where it doesn't change).
            scrollByView.current.set('artist', 0);
            requestAnimationFrame(() => { scrollRef.current?.scrollTo({ top: 0 }); });
        };
        window.addEventListener('mixgoat:tab-retap', onRetap);
        return () => window.removeEventListener('mixgoat:tab-retap', onRetap);
    }, [dispatch]);

    const renderActiveSearch = () => {
        switch (activeSearch) {
            case 'all':
                return <SearchResultsPage query={searchAllQuery} />;
            case 'artist':
                return <ArtistSearch />;
            case 'track':
                return <TrackSearch />;
            case 'mix':
                return <MixSearch />;
            case 'trackList':
                return <TrackListSearch />;
            case 'homeSets':
                return <MixpediaBrowsePage kind="sets" />;
            case 'homeTracks':
                return <MixpediaBrowsePage kind="tracks" />;
            case 'homeArtistTracks':
                return <MixpediaBrowsePage kind="artistTracks" />;
            case 'homeLibraryArtists':
                return <MixpediaLibraryArtistsPage />;
            case 'artistsGrid':
                return <MixpediaArtistsGridPage />;
            case 'label':
                return <LabelSearch />;
            default:
                return <ArtistSearch />;
        }
    };

    return (
        <div className="tab-container djpal-tab">
            <div className="lower-part" ref={scrollRef}>
                {connectFailed && (
                    <div className="connect-banner">
                        <span>Connection failed: {tokenError}</span>
                        <button className="connect-banner-retry" onClick={retryConnect}>Retry</button>
                    </div>
                )}
                {/* Mobile web only: page-local search header. Mobile has no
                    navbar search; NavbarSearch moves inline here (compact,
                    SoundCloud-style — no big page title, just input + list). */}
                {isWeb && (
                    <div className="djpal-mobile-search-header">
                        <NavbarSearch />
                    </div>
                )}
                {viewIsEmpty ? <MixpediaHome /> : renderActiveSearch()}
            </div>
        </div>
    );
};

export default DJPalTab;
